import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSync } from '../context/SyncContext'

interface AccountPanelProps {
  onOpenChange?: (open: boolean) => void
}

export function AccountPanel({ onOpenChange }: AccountPanelProps) {
  const { user, statusLabel, status, error, configured, signIn, signUp, signOut, resetPassword, syncNow } =
    useSync()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const setOpenSafe = (value: boolean) => {
    setOpen(value)
    onOpenChange?.(value)
  }

  useEffect(() => {
    document.documentElement.classList.toggle('account-open', open)
    onOpenChange?.(open)
    return () => {
      document.documentElement.classList.remove('account-open')
    }
  }, [open, onOpenChange])

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setFormError(null)
    try {
      await fn()
      // stay open on success so user sees synced status; close only after sign-in/up if user set
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Fehler')
    } finally {
      setBusy(false)
    }
  }

  const canSubmit =
    email.trim().length > 3 && email.includes('@') && password.length >= 6 && !busy

  const overlay =
    open &&
    createPortal(
      <div className="account-overlay">
        <button
          type="button"
          className="account__scrim"
          aria-label="Konto schliessen"
          onClick={() => setOpenSafe(false)}
        />
        <div
          className="account__panel"
          role="dialog"
          aria-modal="true"
          aria-label="Konto"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="account__panel-head">
            <p className="account__status">{statusLabel}</p>
            <button
              type="button"
              className="account__close"
              aria-label="Schliessen"
              onClick={() => setOpenSafe(false)}
            >
              ✕
            </button>
          </div>

          {!configured && (
            <p className="account__hint">
              Cloud ist noch nicht eingerichtet. Kalender bleibt lokal, bis
              VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY gesetzt sind.
            </p>
          )}

          {configured && !user && (
            <div className="account__form">
              <input
                type="email"
                placeholder="E-Mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <input
                type="password"
                placeholder="Passwort (mind. 6 Zeichen)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              {!canSubmit && (
                <p className="account__hint">E-Mail und Passwort (mind. 6 Zeichen) ausfuellen.</p>
              )}
              <div className="account__actions">
                <button
                  type="button"
                  className="neon-btn neon-btn--primary"
                  disabled={!canSubmit}
                  onClick={() => void run(() => signIn(email.trim(), password))}
                >
                  Anmelden
                </button>
                <button
                  type="button"
                  className="neon-btn neon-btn--ghost"
                  disabled={!canSubmit}
                  onClick={() => void run(() => signUp(email.trim(), password))}
                >
                  Konto erstellen
                </button>
                <button
                  type="button"
                  className="neon-btn neon-btn--ghost"
                  disabled={busy || email.trim().length < 4 || !email.includes('@')}
                  onClick={() =>
                    void run(async () => {
                      await resetPassword(email.trim())
                      setFormError('Reset-Link gesendet — Postfach pruefen.')
                    })
                  }
                >
                  Passwort vergessen
                </button>
              </div>
            </div>
          )}

          {user && (
            <div className="account__actions">
              <p className="account__hint">{user.email}</p>
              <button type="button" className="neon-btn neon-btn--ghost" onClick={() => void syncNow()}>
                Jetzt sync
              </button>
              <button type="button" className="neon-btn neon-btn--danger" onClick={() => void signOut()}>
                Abmelden
              </button>
            </div>
          )}

          {(formError || error) && <p className="account__error">{formError || error}</p>}
        </div>
      </div>,
      document.body,
    )

  return (
    <div className={`account${open ? ' account--open' : ''}`}>
      <button
        type="button"
        className={`account__chip account__chip--${status}`}
        aria-expanded={open}
        onClick={() => setOpenSafe(!open)}
      >
        {user ? user.email?.split('@')[0] : 'Konto'}
        <span className="account__dot" />
      </button>
      {overlay}
    </div>
  )
}
